import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Api as ApiIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  PriorityHigh as PriorityHighIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../store/Index";
import type { EndpointRequest, RequestStatus, Priority, HttpMethod } from "./types";
import apiService from "../../service/Api";
import {
  setRequests,
  updateRequest as updateRequestAction,
  deleteRequest as deleteRequestAction,
  setLoading,
  setError,
} from "../../store/slice/EndpointRequestsSlice";
import { CreateRequestDialog } from "./components/CreateRequestDialog";
import { ViewRequestDialog } from "./components/ViewRequestDialog";
import { DeveloperStatsComponent } from "./components/DeveloperStats";
import { AdminDashboard } from "./components/AdminDashboard";
import { BackendDashboard } from "./components/BackendDashboard";
import { notifyRequestCreated } from "./utils/notifications";
import { isAdmin, isBackendDev, isFrontendDev } from "./utils/permissions";

const STATUS_COLORS: Record<RequestStatus, "success" | "warning" | "info" | "error" | "default"> = {
  completed: "success",
  in_progress: "info",
  pending: "warning",
  blocked: "error",
  cancelled: "default",
};

const PRIORITY_COLORS: Record<Priority, "error" | "warning" | "info" | "default"> = {
  urgent: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: "#4caf50",
  POST: "#2196f3",
  PUT: "#ff9800",
  PATCH: "#9c27b0",
  DELETE: "#f44336",
};

interface EndpointRequestApiItem {
  id: number;
  component_name_snapshot: string;
  task_description: string;
  requested_by: {
    username: string;
    first_name: string;
    last_name: string;
  };
  endpoint_url: string;
  http_method: string;
  status: string;
  priority: string;
  request_date_time: string;
  target_delivery_date_time: string | null;
  payload: Record<string, unknown> | null;
  additional_information: string;
  actual_completion_date?: string;
  status_code?: number;
  challenges?: string;
}

type EndpointRequestsApiResponse =
  | EndpointRequestApiItem[]
  | {
      results: EndpointRequestApiItem[];
    };

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

const EndpointRequestsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const requests = useSelector((state: RootState) => state.endpointRequests.requests);
  // const isLoading = useSelector((state: RootState) => state.endpointRequests.isLoading);
  const [filteredRequests, setFilteredRequests] = useState<EndpointRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");
  const [componentFilter, setComponentFilter] = useState<string>("");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<EndpointRequest | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "pending" | "completed">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    blocked: 0,
  });

  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const location = useLocation();

  const updateNotificationCount = React.useCallback(() => {
    const pendingCount = requests.filter(
      (r) => r.status === "pending" || r.status === "in_progress"
    ).length;
    localStorage.setItem("endpoint_requests_notification_count", pendingCount.toString());
    // Dispatch custom event to update sidebar
    window.dispatchEvent(new CustomEvent("endpoint-requests-updated"));
  }, [requests]);

  const loadRequests = React.useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.get<EndpointRequestsApiResponse>(
        "/api/endpoint-request/endpoint-requests/"
      );

      // Handle both direct array and paginated response formats
      const dataArray: EndpointRequestApiItem[] = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      // Map API response to EndpointRequest format
      const mappedRequests: EndpointRequest[] = dataArray.map((item) => {
        // Helper function to extract date from ISO string
        const extractDate = (dateString: string | null | undefined): string | undefined => {
          if (!dateString) return undefined;
          try {
            return dateString.split("T")[0];
          } catch {
            return undefined;
          }
        };

        return {
          id: item.id.toString(),
          component: item.component_name_snapshot,
          task: item.task_description,
          lead: item.requested_by?.first_name || item.requested_by?.username || "Unknown",
          endpoint: item.endpoint_url || undefined,
          method: (item.http_method as HttpMethod) || undefined,
          status: item.status as RequestStatus,
          priority: item.priority as Priority,
          requestDate: extractDate(item.request_date_time) || new Date().toISOString().split("T")[0],
          targetDeliveryDate: extractDate(item.target_delivery_date_time),
          actualCompletionDate: extractDate(item.actual_completion_date),
          payload: item.payload ? JSON.stringify(item.payload) : undefined,
          description: item.additional_information || undefined,
          createdBy: item.requested_by?.username || "Unknown",
          statusCode: item.status_code,
          challenges: item.challenges,
        };
      });

      dispatch(setRequests(mappedRequests));
    } catch (error: unknown) {
      console.error("Error loading requests:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to load endpoint requests";
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      dispatch(setRequests([]));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const filterRequests = React.useCallback(() => {
    let filtered = [...requests];

    if (activeTab === "pending") {
      filtered = filtered.filter((r) => r.status === "pending" || r.status === "in_progress");
    } else if (activeTab === "completed") {
      filtered = filtered.filter((r) => r.status === "completed");
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter((r) => r.priority === priorityFilter);
    }

    if (componentFilter) {
      filtered = filtered.filter((r) =>
        r.component.toLowerCase().includes(componentFilter.toLowerCase())
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    setFilteredRequests(filtered);
  }, [requests, activeTab, statusFilter, priorityFilter, componentFilter, searchTerm]);

  useEffect(() => {
    loadRequests().catch((error) => {
      console.error("Failed to load requests:", error);
    });
  }, [loadRequests]);



  useEffect(() => {
    if (requests.length > 0) {
      updateNotificationCount();
    }
  }, [requests, updateNotificationCount]);

  useEffect(() => {
    filterRequests();
  }, [filterRequests]);

  // Removed saveRequests - now using Redux directly

  const handleCreateRequest = async (request: Omit<EndpointRequest, "id">) => {
    // The CreateRequestDialog already creates the request via API
    // So we just need to reload the data from the API
    setOpenCreateDialog(false);
    
    // Reload requests from API
    await loadRequests();
    
    // Notify admin and backend users about new request
    notifyRequestCreated({
      id: "new", // Will be updated after reload
      component: request.component,
      task: request.task,
      createdBy: user?.username || "Unknown",
    });
    
    // Show toast notification
    toast.info(`New endpoint request created: ${request.component}`);
    
    // Update gamification stats
    window.dispatchEvent(new CustomEvent("endpoint-request-created", { 
      detail: { username: user?.username } 
    }));
  };

  const handleUpdateRequest = (id: string, updates: Partial<EndpointRequest>) => {
    const request = requests.find((r) => r.id === id);
    dispatch(updateRequestAction({ id, updates }));
    
    // Notify if status changed to completed
    if (updates.status === "completed" && request?.status !== "completed") {
      // Notification will be handled by BackendDashboard component
      toast.success(`Endpoint request completed: ${request?.component}`);
    }
  };

  const handleDeleteRequest = (id: string) => {
    dispatch(deleteRequestAction(id));
    setOpenViewDialog(false);
    setSelectedRequest(null);
  };

  const handleViewRequest = (request: EndpointRequest) => {
    setSelectedRequest(request);
    setOpenViewDialog(true);
  };


  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon fontSize="small" />;
      case "in_progress":
        return <PlayArrowIcon fontSize="small" />;
      case "pending":
        return <PendingIcon fontSize="small" />;
      case "blocked":
        return <BlockIcon fontSize="small" />;
      case "cancelled":
        return <CancelIcon fontSize="small" />;
    }
  };

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Check user role based on permissions from Redux: state.auth.user.permissions
  // Permissions are stored as strings array with full_codename format: "endpoint_request.add_component"
  const userIsAdmin = isAdmin(user);
  const userIsBackendDev = isBackendDev(user);
  const userIsFrontendDev = isFrontendDev(user);

  // Determine which view to render based on the route path. This ensures only
  // the intended dashboard is shown when visiting `/endpoint-requests/frontend`
  // or `/endpoint-requests/backend`. For the base `/endpoint-requests` path we
  // prefer Admin (if available), then Frontend, then Backend.
  const routePath = location.pathname || "";
  let renderAdmin = false;
  let renderBackend = false;
  let renderFrontend = false;

  if (routePath.endsWith("/frontend")) {
    renderFrontend = userIsFrontendDev;
  } else if (routePath.endsWith("/backend")) {
    renderBackend = userIsBackendDev;
  } else {
    // default/root: show admin if admin, otherwise frontend if frontend dev,
    // otherwise backend if backend dev.
    if (userIsAdmin) renderAdmin = true;
    else if (userIsFrontendDev) renderFrontend = true;
    else if (userIsBackendDev) renderBackend = true;
  }

  // Load stats from API for developers
  const loadStats = React.useCallback(async () => {
    if (!userIsFrontendDev) return;
    
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats({
        total: response.data.total_requests || 0,
        pending: (response.data.status_counts.pending || 0) + (response.data.status_counts.in_progress || 0),
        completed: response.data.status_counts.completed || 0,
        blocked: response.data.status_counts.blocked || 0,
      });
    } catch (error: unknown) {
      console.error("Error loading stats:", error);
      // Fallback to calculating from requests
      setStats({
        total: requests.length,
        pending: requests.filter((r) => r.status === "pending" || r.status === "in_progress").length,
        completed: requests.filter((r) => r.status === "completed").length,
        blocked: requests.filter((r) => r.status === "blocked").length,
      });
    }
  }, [userIsFrontendDev, requests]);

  useEffect(() => {
    if (userIsFrontendDev) {
      loadStats();
    }
  }, [userIsFrontendDev, loadStats]);

  // Reload stats when requests change
  useEffect(() => {
    if (userIsFrontendDev && requests.length > 0) {
      loadStats();
    }
  }, [requests.length, userIsFrontendDev, loadStats]);

  // Listen for notifications (for frontend developers)
  useEffect(() => {
    if (!userIsFrontendDev) return;

    const handleNotification = (event: CustomEvent) => {
      const notification = event.detail;
      if (notification.type === "request_completed") {
        // Only show if this request was created by the current user
        if (notification.createdBy === user?.username) {
          toast.success(
            `Your endpoint request completed: ${notification.component} - Endpoint: ${notification.endpoint}`,
            {
              autoClose: 6000,
            }
          );
        }
      }
    };

    window.addEventListener("endpoint-request-notification", handleNotification as EventListener);
    return () => {
      window.removeEventListener("endpoint-request-notification", handleNotification as EventListener);
    };
  }, [userIsFrontendDev, user?.username]);

  // Ensure that if a user tries to access a restricted route they don't have access to,
  // nothing renders (this prevents unauthorized viewing). If no permission matches
  // the requested request, render nothing. This check happens AFTER hooks are initialized.
  const hasAccessToCurrentRoute =
    (routePath.endsWith("/frontend") && userIsFrontendDev) ||
    (routePath.endsWith("/backend") && userIsBackendDev) ||
    (routePath === "/endpoint-requests" && (userIsAdmin || userIsFrontendDev || userIsBackendDev));

  // If user is trying to access a route they don't have permission for, show access denied.
  if (!hasAccessToCurrentRoute) {
    return (
      <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h5" color="error" fontWeight="bold">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {"You don't have permission to view this page."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
      {/* Render only ONE dashboard based on role and route - using if/else to ensure mutual exclusivity */}
      {renderAdmin && !renderBackend && !renderFrontend && (
        <AdminDashboard
          requests={requests}
          onRequestUpdate={handleUpdateRequest}
        />
      )}

      {renderBackend && !renderAdmin && !renderFrontend && (
        <BackendDashboard
          requests={requests}
          onRequestUpdate={handleUpdateRequest}
          onReloadRequests={loadRequests}
        />
      )}

      {renderFrontend && !renderAdmin && !renderBackend && (
      <>
     
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: "text.primary" }}>
              Developers TAB
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track API endpoint requests between frontend and backend teams
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{
              backgroundColor: primaryColor,
              "&:hover": { backgroundColor: primaryColor, opacity: 0.9 },
              boxShadow: 2,
              px: 3,
              py: 1.5,
            }}
          >
            New Request
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                backgroundColor: primaryColor,
                color: "white",
                boxShadow: 3,
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Total Requests
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.total}
                    </Typography>
                  </Box>
                  <ApiIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "warning.main" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Pending
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="warning.main">
                      {stats.pending}
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40, color: "warning.main", opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "success.main" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Completed
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {stats.completed}
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "error.main" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Blocked
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="error.main">
                      {stats.blocked}
                    </Typography>
                  </Box>
                  <BlockIcon sx={{ fontSize: 40, color: "error.main", opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Developer Stats / Gamification */}
        {user?.username && (
          <DeveloperStatsComponent requests={requests} currentUser={user.username} />
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3, boxShadow: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 64,
              },
            }}
          >
            <Tab label={`All (${stats.total})`} value="all" />
            <Tab label={`Pending (${stats.pending})`} value="pending" />
            <Tab label={`Completed (${stats.completed})`} value="completed" />
          </Tabs>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 2.5, mb: 3, boxShadow: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search by component, task, lead, or endpoint..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as RequestStatus | "all")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value as Priority | "all")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                placeholder="Component"
                value={componentFilter}
                onChange={(e) => setComponentFilter(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setStatusFilter("all");
                  setPriorityFilter("all");
                  setComponentFilter("");
                  setSearchTerm("");
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper sx={{ boxShadow: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lead</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Endpoint</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No requests found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      hover
                      onClick={() => handleViewRequest(request)}
                      sx={{
                        "&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {request.component}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={request.task}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {request.task}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<PersonIcon />}
                          label={request.lead}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {request.method && (
                            <Chip
                              label={request.method}
                              size="small"
                              sx={{
                                backgroundColor: METHOD_COLORS[request.method as HttpMethod] || "#666",
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {request.endpoint || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(request.status)}
                          label={request.status.replace("_", " ").toUpperCase()}
                          color={STATUS_COLORS[request.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<PriorityHighIcon />}
                          label={request.priority.toUpperCase()}
                          color={PRIORITY_COLORS[request.priority]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CalendarIcon fontSize="small" sx={{ opacity: 0.7 }} />
                          <Typography variant="body2" fontSize="0.875rem">
                            {new Date(request.requestDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewRequest(request);
                          }}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRequests.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>

        {/* Create Dialog */}
        <CreateRequestDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          onSubmit={handleCreateRequest}
        />

        {/* View/Edit Dialog */}
        {selectedRequest && (
          <ViewRequestDialog
            open={openViewDialog}
            request={selectedRequest}
            onClose={() => {
              setOpenViewDialog(false);
              setSelectedRequest(null);
            }}
            onUpdate={handleUpdateRequest}
            onDelete={handleDeleteRequest}
          />
        )}
      </>
      )}
    </Box>
  );
};

export default EndpointRequestsPage;
